<script setup lang="ts">
import { message } from 'ant-design-vue'
import { computedStatusColor, formatPhoneNumber } from '~/utils/utils-js'
import { getSecretNumber } from '~/server/newhouse'
import { secretNumberTrackingSave } from '~/server/common'

interface dataType {
  floor: string
  saleStatusId: number
  saleStatus: string
  floorId: number
  cityId: number
  filing: string
  floorLabel: string
  isFavorite: number
  propertyType: string
}

const props = defineProps({
  floorInfo: {
    type: Object as () => dataType,
    default: () => ({}),
  },
})
const likeRef = ref<any>()
const statusColor = computed(() => computedStatusColor(props.floorInfo))

const LazyWxQrImage = defineAsyncComponent(() => import('@/components/WechatDial/WxQrImage.vue'))

// 隐私号电话
const privacyNumber = ref<string>('')
// 隐私号提示
const privacyErrTip = ref<string>('')
// 获取隐私号
const userStore = useUserInfoStore()
const nuxtApp = useNuxtApp()
const uuid = ref('')
const route = useRoute()

/**
 * setSecretFloorPhone： 存储隐私号到cookie，并设置10分钟的有效时间。此方法在获取隐私号成功后调用
 * getSecretFloorPhone： 获取隐私号，返回{  phone, countdown }，phone为隐私号,countdown为倒计时剩余秒数，在页面打开的时候调用
 * countDown： 给定一个倒计时函数，在倒计时结束后调用，在需要自定义执行函数的时候调用，这里的 两处 我需要在倒计时结束后清空隐私号
 */
const { setSecretFloorPhone, getSecretFloorPhone, countDown } = useCommon()
getSecretFloorPhone(props.floorInfo?.floorId).then((res: any) => {
  if (res) {
    const { phone, countdown } = res
    privacyNumber.value = phone
    countDown(() => {
      privacyNumber.value = ''
      privacyErrTip.value = ''
    }, countdown)
  }
})

function handlePrivacyNumber() {
  if (privacyNumber.value || privacyErrTip.value)
    return
  getSecretNumber({ floorId: props.floorInfo?.floorId, cityId: props.floorInfo?.cityId, type: 1 }).then((res) => {
    const { code, dataBody, message: msg } = res
    if (code === 0) {
      if (!dataBody) {
        message.warning('当前线路忙，请稍后再拨')
        return
      }
      const { phoneX, obtainResult } = res.dataBody

      /**
       * setSecretFloorPhone缓存十分钟，并且开启倒计时
       * 十分钟后隐私号时效，清空隐私号countDown
       */
      setSecretFloorPhone(props.floorInfo?.floorId, phoneX)
      countDown(() => {
        privacyNumber.value = ''
        privacyErrTip.value = ''
      })

      privacyNumber.value = phoneX
      // 埋点统计
      const { userId } = userStore?.userInfo ?? {}
      uuid.value = nuxtApp.$getUuid()
      const { params } = route
      const { floorId, houseModelId, floorHouseId } = params
      // 隐私号埋点统计
      secretNumberTrackingSave({
        businessDataId: 1,
        businessId: props.floorInfo.floorId,
        cityId: props.floorInfo.cityId,
        equipmentBrowseUuid: uuid.value,
        numberId: floorHouseId || houseModelId || floorId || props.floorInfo.floorId,
        obtainResult,
        pageName: document.title,
        pageUrl: `${window.location.origin}${route.fullPath}`,
        portTypeId: 1,
        sectionId: 1,
        userId: userId || 0,
      })
    }
    else if ([1407001, 1407002, 1407003].includes(code)) {
      privacyErrTip.value = msg
    }
    else { message.warning(msg) }
  })
}
onMounted(() => {
  //  是否已关注，客户端二次校验
  userSaveCheck({
    numberId: [props.floorInfo.floorId],
    sortId: 5,
    cb: (res) => {
      const { code, dataBody } = res
      if (code === 0) {
        likeRef.value.setLike(dataBody[props.floorInfo.floorId])
      }
    },
  })
})
</script>

<template>
  <div class="floor-phone mt-15px mb-15px">
    <div class="left-box">
      <h1 class="floor-title">
        {{ floorInfo?.floor }} <small v-if="floorInfo?.filing" class="filing">备案名：{{ floorInfo?.filing }}</small>
      </h1>
      <div class="follow-tag">
        <!--        对接关注★★★wzy -->
        <like
          ref="likeRef"
          :is-collect="floorInfo?.isFavorite" :data="floorInfo" :title="floorInfo?.floor" :number-id="floorInfo?.floorId" :text="['关注', '取消关注']"
        />
        <!--        <div class="follow"> -->
        <!--          关注 -->
        <!--          <HeartOutlined /> -->
        <!--          &lt;!&ndash;          <HeartFilled style="color: red" /> &ndash;&gt; -->
        <!--        </div> -->
        <tag v-if="floorInfo?.saleStatusId" :color="statusColor">
          {{ floorInfo?.saleStatus }}
        </tag>
        <tag v-for="item in floorInfo?.propertyType?.split(',')" :key="item">
          {{ item }}
        </tag>
        <template v-if="floorInfo?.floorLabel">
          <tag v-for="item in floorInfo?.floorLabel?.split(',')" :key="item">
            {{ item }}
          </tag>
        </template>
      </div>
    </div>
    <div class="right-box">
      <div class="phone">
        <!--        <PhoneFilled style="color: #52BE54;transform: scaleX(-1);font-size: 35px" /> -->
        <i v-show="!privacyErrTip" style="color: #52BE54;font-size: 35px;margin: 0 5px 0 5px" class="iconfont icon-dianhua4" />
        <div class="text" @click="handlePrivacyNumber">
          <div class="show-phone">
            {{ privacyErrTip || formatPhoneNumber(privacyNumber) || '点击查看电话' }}
          </div>
          <div v-show="!privacyErrTip" class="scan-code">
            {{ privacyNumber ? '请尽快拨打，如拨打失效请刷新' : '扫码致电售楼处，了解更多优惠' }}
          </div>
        </div>
        <div class="qr-code">
          <ClientOnly>
            <LazyWxQrImage v-bind="floorInfo" />
          </ClientOnly>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.floor-phone {
  width: 100%;

  color: rgba(27, 180, 64, 0.1);

  .left-box {
    float: left;

    .floor-title {
      font-size: 30px;
      color: #3D3D3D;
      font-weight: bold;

      .filing {
        font-size: 14px;
        color: #9C9C9C;
        font-weight: normal;
        margin-left: 5px;
      }
    }

    .follow-tag {
      margin-top: 17px;
      width: 700px;
      @include ell();

      .follow {
        display: inline-block;
        font-size: 14px;
        color: #616669;
        margin-right: 10px;
      }
    }
  }

  .right-box {
    float: right;
    position: relative;

    .phone {
      display: flex;
      align-items: center;
      width: 355px;
      height: 85px;
      background: #FAFAFA;
      margin-right: 20px;
      border-bottom-left-radius: 85px;
      border-top-left-radius: 85px;
      padding-left: 20px;
    }

    .text {
      margin-left: 10px;
      cursor: pointer;

      .show-phone {
        font-size: 20px;
        color: #52BE54;
        font-weight: bold;
        line-height: 30px;
      }

      .scan-code {
        font-size: 12px;
        color: #9C9C9C;
        line-height: 30px;
      }
    }

    .qr-code {
      position: absolute;
      right: 0;
      width: 100px;
      height: 100px;
      border-radius: 50%;
      overflow: hidden;
      background-color: #ffffff;

      .qr-code-img {
        width: 100%;
        height: 100%;
      }

      //img {
      //  width: 100%;
      //  //height: 100%;
      //}
    }
  }

  &::after {
    content: '';
    display: block;
    clear: both;
  }
}
</style>
